import { z } from 'zod';

/**
 * ISO 4217 currency value objects.
 *
 * A {@link Currency} describes a denomination — code, display name, symbol,
 * and minor-unit exponent — and nothing else: no amounts, no FX. Reflex
 * records and computes every amount in USD today (see the money doc:
 * `docs/internal/architecture/money.md`); this registry exists so a
 * non-USD amount arriving at a boundary can be recognized, named, and
 * rejected (or, in the future, translated) instead of silently misread as
 * dollars.
 *
 * @module @reflex/shared/currency
 */

/**
 * The shape of an ISO 4217 alphabetic code: exactly three uppercase ASCII
 * letters. Shape-valid does not mean known — resolve through
 * {@link getCurrency} for a registered denomination.
 */
export const CurrencyCodeSchema = z.string().regex(/^[A-Z]{3}$/);
export type CurrencyCode = z.infer<typeof CurrencyCodeSchema>;

/** One ISO 4217 denomination. */
export const CurrencySchema = z.object({
  /** ISO 4217 alphabetic code, e.g. `'USD'`. */
  currencyCode: CurrencyCodeSchema,
  /** English display name, e.g. `'US Dollar'`. */
  name: z.string().min(1),
  /** Conventional symbol, e.g. `'$'`. Falls back to the code where none exists. */
  symbol: z.string().min(1),
  /**
   * ISO 4217 minor-unit exponent: how many decimal digits one unit
   * subdivides into (USD 2 → cents, JPY 0, BHD 3).
   */
  minorUnits: z.number().int().nonnegative(),
  /** ISO 4217 three-digit numeric code, e.g. `'840'` for USD. */
  numericCode: z.string().regex(/^\d{3}$/),
});
export type Currency = z.infer<typeof CurrencySchema>;

/**
 * The denominations Reflex knows by name. USD is the only one the backend
 * ever records; the rest exist so provider-reported non-USD amounts can be
 * identified at the boundary. Extend the list freely — adding a row here
 * grants recognition, not permission to record in it.
 */
export const CURRENCIES = {
  USD: { currencyCode: 'USD', name: 'US Dollar', symbol: '$', minorUnits: 2, numericCode: '840' },
  EUR: { currencyCode: 'EUR', name: 'Euro', symbol: '€', minorUnits: 2, numericCode: '978' },
  GBP: {
    currencyCode: 'GBP',
    name: 'Pound Sterling',
    symbol: '£',
    minorUnits: 2,
    numericCode: '826',
  },
  JPY: {
    currencyCode: 'JPY',
    name: 'Japanese Yen',
    symbol: '¥',
    minorUnits: 0,
    numericCode: '392',
  },
  CHF: {
    currencyCode: 'CHF',
    name: 'Swiss Franc',
    symbol: 'CHF',
    minorUnits: 2,
    numericCode: '756',
  },
  CAD: {
    currencyCode: 'CAD',
    name: 'Canadian Dollar',
    symbol: 'CA$',
    minorUnits: 2,
    numericCode: '124',
  },
  AUD: {
    currencyCode: 'AUD',
    name: 'Australian Dollar',
    symbol: 'A$',
    minorUnits: 2,
    numericCode: '036',
  },
  NZD: {
    currencyCode: 'NZD',
    name: 'New Zealand Dollar',
    symbol: 'NZ$',
    minorUnits: 2,
    numericCode: '554',
  },
  CNY: {
    currencyCode: 'CNY',
    name: 'Yuan Renminbi',
    symbol: 'CN¥',
    minorUnits: 2,
    numericCode: '156',
  },
  HKD: {
    currencyCode: 'HKD',
    name: 'Hong Kong Dollar',
    symbol: 'HK$',
    minorUnits: 2,
    numericCode: '344',
  },
  SGD: {
    currencyCode: 'SGD',
    name: 'Singapore Dollar',
    symbol: 'S$',
    minorUnits: 2,
    numericCode: '702',
  },
  INR: {
    currencyCode: 'INR',
    name: 'Indian Rupee',
    symbol: '₹',
    minorUnits: 2,
    numericCode: '356',
  },
  KRW: {
    currencyCode: 'KRW',
    name: 'South Korean Won',
    symbol: '₩',
    minorUnits: 0,
    numericCode: '410',
  },
  BRL: {
    currencyCode: 'BRL',
    name: 'Brazilian Real',
    symbol: 'R$',
    minorUnits: 2,
    numericCode: '986',
  },
  MXN: {
    currencyCode: 'MXN',
    name: 'Mexican Peso',
    symbol: 'MX$',
    minorUnits: 2,
    numericCode: '484',
  },
  SEK: {
    currencyCode: 'SEK',
    name: 'Swedish Krona',
    symbol: 'kr',
    minorUnits: 2,
    numericCode: '752',
  },
  NOK: {
    currencyCode: 'NOK',
    name: 'Norwegian Krone',
    symbol: 'kr',
    minorUnits: 2,
    numericCode: '578',
  },
  DKK: {
    currencyCode: 'DKK',
    name: 'Danish Krone',
    symbol: 'kr',
    minorUnits: 2,
    numericCode: '208',
  },
  PLN: {
    currencyCode: 'PLN',
    name: 'Polish Złoty',
    symbol: 'zł',
    minorUnits: 2,
    numericCode: '985',
  },
  ZAR: {
    currencyCode: 'ZAR',
    name: 'South African Rand',
    symbol: 'R',
    minorUnits: 2,
    numericCode: '710',
  },
} as const satisfies Record<CurrencyCode, Currency>;

/** A code with a row in {@link CURRENCIES}. */
export type KnownCurrencyCode = keyof typeof CURRENCIES;

/** The denomination every Reflex amount is recorded in. */
export const USD: Currency = CURRENCIES.USD;

/**
 * Resolve a code to its registered denomination, case-insensitively.
 * Returns `null` for anything not in {@link CURRENCIES} — including
 * shape-valid ISO codes Reflex has no row for.
 */
export function getCurrency(code: string): Currency | null {
  const normalized = code.toUpperCase();
  return normalized in CURRENCIES ? CURRENCIES[normalized as KnownCurrencyCode] : null;
}
